// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.
import type { SemVer } from "./types.ts";
import { parseBuild, parseNumber, parsePrerelease } from "./_shared.ts";
import { FULL_REGEXP, MAX_LENGTH } from "./_shared.ts";

/**
 * Attempt to parse a string as a semantic version, returning a SemVer object.
 *
 * @example Usage
 * ```ts
 * import { parse } from "@std/semver/parse";
 * import { assertEquals } from "@std/assert";
 *
 * const version = parse("1.2.3");
 * assertEquals(version, {
 *   major: 1,
 *   minor: 2,
 *   patch: 3,
 *   prerelease: [],
 *   build: [],
 * });
 * ```
 *
 * @throws {TypeError} If the input string is invalid.
 * @param value The version string to parse
 * @returns A valid SemVer
 */
export function parse(value: string): SemVer {
  if (typeof value !== "string") {
    throw new TypeError(
      `Cannot parse version as version must be a string: received ${typeof value}`,
    );
  }

  if (value.length > MAX_LENGTH) {
    throw new TypeError(
      `Cannot parse version as version length is too long: length is ${value.length}, max length is ${MAX_LENGTH}`,
    );
  }

  value = value.trim();

  const groups = value.match(FULL_REGEXP)?.groups;
  if (!groups) throw new TypeError(`Cannot parse version: ${value}`);

  const major = parseNumber(
    groups.major!,
    `Cannot parse version ${value}: invalid major version`,
  );
  const minor = parseNumber(
    groups.minor!,
    `Cannot parse version ${value}: invalid minor version`,
  );
  const patch = parseNumber(
    groups.patch!,
    `Cannot parse version ${value}: invalid patch version`,
  );

  const prerelease = groups.prerelease
    ? parsePrerelease(groups.prerelease)
    : [];
  const build = groups.buildmetadata ? parseBuild(groups.buildmetadata) : [];

  return { major, minor, patch, prerelease, build };
}

// denoCacheMetadata={"headers":{"x-jsr-cache-id":"IAD","content-type":"text/typescript","x-goog-stored-content-encoding":"identity","content-length":"1887","x-goog-metageneration":"1","expires":"Sat, 08 Nov 2025 09:36:15 GMT","x-goog-hash":"crc32c=On3Qtw==,md5=9cQ0gtZ9BYmUA2iMFE+FmQ==","x-goog-storage-class":"STANDARD","x-goog-generation":"1725446502105069","x-goog-stored-content-length":"1887","server":"UploadServer","via":"1.1 google","date":"Fri, 08 Nov 2024 09:36:15 GMT","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-jsr-cache-status":"hit","x-robots-tag":"noindex","cross-origin-resource-policy":"cross-origin","x-content-type-options":"nosniff","age":"2026","access-control-allow-origin":"*","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","cache-control":"public, max-age=31536000, immutable","etag":"\"f5c43482d67d05899403688c144f8599\"","x-guploader-uploadid":"AHmUCY1TjOtGCQ4KKN5JXe4gb9wk8kF-sZr2JS0w3EI4HUON3tiSprS9AjLTdVn_n9iwYii5ogU","accept-ranges":"bytes","access-control-expose-headers":"*","last-modified":"Wed, 04 Sep 2024 10:41:42 GMT"},"url":"https://jsr.io/@std/semver/1.0.3/parse.ts","time":1731060601}